import type { UnknownAction } from '@reduxjs/toolkit';
import { sharedAccountsListen } from '@skynet/data-shared-accounts';
import { ListenSection } from 'src/hooks/useProjectSwitch/listen-sections';
import type { KnownListenSection } from 'src/hooks/useProjectSwitch/listen-sections';
import { listen as listenLoans } from 'src/store/loans/actions';
import { listen as listenRetainers } from 'src/store/retainers/actions';
import { startAppListening } from 'src/store/middleware/listenerMiddleware';

/** Listen sections whose entry reloads the shared (loans/retainers) accounts. */
const ACCOUNTS_LISTEN_SECTIONS: readonly KnownListenSection[] = [
  ListenSection.Overview,
  ListenSection.Balances,
];

type DomainListenAction = ReturnType<typeof listenLoans> | ReturnType<typeof listenRetainers>;

// `isAnyOf` cannot narrow `createMmWsAction` creators (their type is `ReturnType<typeof
// createAction<...>>`), so the union is spelled out in a type guard.
function isDomainListen(action: UnknownAction): action is DomainListenAction {
  return listenLoans.match(action) || listenRetainers.match(action);
}

function isAccountsListenSection(section: string): boolean {
  return ACCOUNTS_LISTEN_SECTIONS.some((accountsSection) => accountsSection === section);
}

/**
 * Bridges the loans and retainers listen actions to the shared accounts slice. The slice lives in
 * `@skynet/data-shared-accounts`, which cannot import the app-level listen actions or know the
 * route vocabulary, so the section filter lives here. The domain listen already went over the
 * wire; the bridged action is state-only.
 */
export function startSharedAccountsListen() {
  startAppListening({
    matcher: isDomainListen,
    effect: (action, listenerApi) => {
      const { section } = action.payload;
      if (!isAccountsListenSection(section)) return;

      listenerApi.dispatch(
        sharedAccountsListen({ section }, { isInitCall: action.meta.isInitCall === true }),
      );
    },
  });
}
